import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart } from '@mui/x-charts-pro';
import { Box } from '@mui/material';
import type { ApiResourceOverviewBucket } from '@repo/api-contract';

const CHART_HEIGHT = 150;

type OverviewMiniChartProps = {
  buckets: ApiResourceOverviewBucket[];
  color: string;
  countLabel: string;
};

function formatBucketDate(dateStr: string, style: 'short' | 'long') {
  const date = new Date(`${dateStr}T00:00:00`);
  if (style === 'short') {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function OverviewMiniChart({
  buckets,
  color,
  countLabel,
}: OverviewMiniChartProps) {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.ReportsPage',
  });
  const xData = useMemo(
    () => buckets.map((bucket) => bucket.formattedBucketDate),
    [buckets],
  );
  const yData = useMemo(() => buckets.map((bucket) => bucket.value), [buckets]);

  return (
    <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
      <LineChart
        height={CHART_HEIGHT}
        series={[
          {
            data: yData,
            curve: 'monotoneX',
            showMark: true,
            color,
            label: countLabel,
            valueFormatter: (value) =>
              value === null ? '' : `${value} ${countLabel}`,
          },
        ]}
        xAxis={[
          {
            data: xData,
            scaleType: 'point',
            disableLine: true,
            tickLabelStyle: { fontSize: 11 },
            valueFormatter: (value, context) => {
              const dateStr = String(value);
              if (context.location === 'tick') {
                return formatBucketDate(dateStr, 'short');
              }
              return t('Week of {{date}}', {
                date: formatBucketDate(dateStr, 'long'),
              });
            },
          },
        ]}
        yAxis={[
          {
            disableLine: true,
            width: 36,
            tickLabelStyle: { fontSize: 11 },
            valueFormatter: (value: number) => String(value),
          },
        ]}
        margin={{ top: 12, right: 12, bottom: 4, left: 0 }}
        grid={{ horizontal: true, vertical: false }}
        sx={{
          '& .MuiLineElement-root': {
            strokeWidth: 2.5,
          },
          '& .MuiChartsLegend-root': {
            display: 'none',
          },
          '& .MuiChartsGrid-line': {
            strokeDasharray: '4 4',
            strokeOpacity: 0.25,
          },
        }}
      />
    </Box>
  );
}
